import React, { useMemo, useCallback, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaCheck, FaTimes } from "react-icons/fa";
import getProductImageUrl, {
  resolveProductImagePath,
} from "../../utils/imageUtils";
import { getMatchPercent } from "../../utils/matchScoreUtils";
import {
  buildMatchPriceSummary,
  buildMatchTradeOff,
} from "../../utils/matchTradeoffUtils";
import {
  buildMatchTagLabels,
  fallbackMatchExplanation,
  normalizeMatchExplanation,
  sanitizeMatchExplanationForProduct,
} from "../../utils/matchDisplayUtils";
import useEnrichedMatchProducts from "../../hooks/useEnrichedMatchProducts";
import styles from "../../styles/components/feed/FeedMatchSection.module.css";
import MarketplaceListingsSection from "../marketplace/MarketplaceListingsSection";
import FeedMatchFusedLine from "./FeedMatchFusedLine";
import { getDecisionStageLabel } from "../../utils/activeDecisionUtils";
import { buildProductLinkLabel } from "../../utils/matchCardA11y";
import {
  addDismissedMatchProductId,
  readDismissedMatchProductIds,
  readHomeChatMatchSessionId,
} from "../../utils/homeChatMatchPersistence";
import usePlatformEvents from "../../hooks/usePlatformEvents";
import { useUserAuth } from "../../contexts/UserAuthContext";
import api from "../../utils/api/axiosInstance";

export const HOME_MATCH_RESULTS_LIMIT = 3;

const getSortableMatchPercent = (product) => {
  const raw = product?.matchScore ?? product?.score ?? product?.match;
  if (raw == null || !Number.isFinite(Number(raw))) return -1;
  return getMatchPercent({ matchScore: raw });
};

const sortByRankHighToLow = (products) => {
  return [...products].sort((a, b) => {
    const scoreDiff =
      getSortableMatchPercent(b) - getSortableMatchPercent(a);
    if (scoreDiff !== 0) return scoreDiff;
    const rankA = a.rank != null ? Number(a.rank) : 99;
    const rankB = b.rank != null ? Number(b.rank) : 99;
    if (rankA !== rankB) return rankA - rankB;
    const idA = String(a._id || a.id || "");
    const idB = String(b._id || b.id || "");
    if (idA === idB) return 0;
    return idA < idB ? -1 : 1;
  });
};

const getProductPath = (product) => {
  const catId = product?.category?._id || product?.category;
  if (catId) return `/category/${catId}/product/${product._id}`;
  return `/product/${product._id}`;
};

const formatMatchCardPrice = (product) => {
  const price = Number(product?.price);
  if (!Number.isFinite(price)) return null;
  return `$${price.toLocaleString()}`;
};

const buildMatchTags = (product, searchParams) => {
  const labels = buildMatchTagLabels(searchParams);
  const tags = labels.map((label) => ({ label, variant: "purple" }));

  if (
    product?.price != null &&
    searchParams?.budget?.max != null &&
    tags.length < 3 &&
    !labels.some((label) => label.startsWith("under $"))
  ) {
    tags.push({ label: "In budget", variant: "mint" });
  }

  return tags.slice(0, 3);
};

const BestMatchCard = ({
  product,
  siblings = [],
  searchParams,
  rank,
  matchScoreRevision = 0,
  featured = false,
  heroSurface = false,
  topPickProduct = null,
  onDismiss = null,
}) => {
  const rawReason =
    product.matchExplanation && typeof product.matchExplanation === "string"
      ? sanitizeMatchExplanationForProduct(
          product.matchExplanation,
          product,
          siblings,
          searchParams,
        )
      : null;
  // Only replace contaminated blurbs — don't invent copy when none was provided.
  const hadExplanation =
    typeof product.matchExplanation === "string" &&
    Boolean(product.matchExplanation.trim());
  const groundedFallback =
    hadExplanation && !rawReason
      ? fallbackMatchExplanation(product, searchParams)
      : null;
  const reason = normalizeMatchExplanation(
    rawReason || groundedFallback,
    searchParams,
  );
  const tags = buildMatchTags(product, searchParams);
  const priceLabel = formatMatchCardPrice(product);
  const productPath = getProductPath(product);
  const tradeOff =
    !featured && topPickProduct
      ? buildMatchTradeOff(product, topPickProduct)
      : null;

  const renderHeroActions = () => (
    <div className={styles.heroActions}>
      <Link to={productPath} className={styles.heroActionPrimary}>
        View details
      </Link>
    </div>
  );

  const productLinkLabel = buildProductLinkLabel(product, { priceLabel, rank });
  const dismissLabel = `Not interested in ${product?.name || "this match"}`;

  const matchDetailsContent = (
    <>
      {reason && (
        <p className={styles.matchCardWhy}>
          <span className={styles.matchCardWhyLabel}>
            Why this matches you:
          </span>{" "}
          {reason}
        </p>
      )}
      <FeedMatchFusedLine
        product={product}
        wizardRevision={matchScoreRevision}
        forceAligned
        className={styles.matchCardFusedLine}
      />
      {!featured && (
        <p
          className={
            tradeOff
              ? styles.matchCardTradeOff
              : styles.matchCardTradeOffPlaceholder
          }
          aria-hidden={!tradeOff}
        >
          {tradeOff || "\u00a0"}
        </p>
      )}
    </>
  );

  return (
    <article
      className={`${styles.matchCard} ${styles.matchFeedCard} ${
        featured ? styles.matchCardFeatured : ""
      } ${heroSurface ? styles.matchCardFeaturedHero : ""}`.trim()}
    >
      {typeof onDismiss === "function" && (
        <button
          type="button"
          className={styles.matchCardDismiss}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDismiss(product);
          }}
          aria-label={dismissLabel}
          title="Not interested"
        >
          <FaTimes aria-hidden="true" />
        </button>
      )}
      <Link
        to={getProductPath(product)}
        className={styles.matchCardLink}
        aria-label={productLinkLabel}
      >
        <div className={styles.matchCardImageWrap}>
          <img
            src={getProductImageUrl(resolveProductImagePath(product))}
            alt=""
            className={styles.matchCardImage}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = getProductImageUrl();
            }}
          />
          <span
            className={`${styles.matchCardRankBadge} ${
              rank === 1 ? styles.matchCardRankBadgeTop : ""
            }`}
            aria-hidden="true"
          >
            {rank}
          </span>
          <span className={styles.matchCardCornerIcon} aria-hidden="true">
            <FaCheck />
          </span>
        </div>
        <div className={styles.matchCardBody}>
          <div className={styles.matchCardHeader}>
            <h3 className={styles.matchCardName} aria-hidden="true">
              {product.name}
            </h3>
            {priceLabel && (
              <span className={styles.matchCardPrice} aria-hidden="true">
                {priceLabel}
              </span>
            )}
          </div>
          {heroSurface && (
            <div className={styles.matchCardDetails}>{matchDetailsContent}</div>
          )}
        </div>
      </Link>
      {!heroSurface && (
        <div className={styles.matchCardDetails}>{matchDetailsContent}</div>
      )}
      {tags.length > 0 && (
        <div className={`${styles.matchCardTags} ${styles.matchCardTagsPad}`}>
          {tags.map((tag) => (
            <span
              key={tag.label}
              className={`${styles.matchTag} ${
                tag.variant === "mint"
                  ? styles.matchTagMint
                  : styles.matchTagPurple
              }`}
            >
              {tag.label}
            </span>
          ))}
        </div>
      )}
      {heroSurface ? (
        renderHeroActions()
      ) : (
        <Link to={productPath} className={styles.compareOptionsBtn}>
          View details
        </Link>
      )}
    </article>
  );
};

const FeedMatchSection = ({
  products = [],
  loading = false,
  searchParams = null,
  matchScoreRevision = 0,
  activeDecision = null,
  emptyHintClassName = "",
  variant = "default",
  mode = "full",
  showMarketplace = true,
  skipFeaturedProduct = false,
  sectionTitle: sectionTitleProp,
  onShortlistExhausted = null,
  loadError = null,
  onRetryLoad = null,
}) => {
  const isFeatured = mode === "featured";
  const isContinued = mode === "continued";
  const decisionLabel = getDecisionStageLabel(activeDecision?.stage);
  const enrichedProducts = useEnrichedMatchProducts(products);
  const { user } = useUserAuth();
  const { trackProduct } = usePlatformEvents();
  const [dismissedIds, setDismissedIds] = useState(
    () => new Set(readDismissedMatchProductIds()),
  );
  const navigate = useNavigate();

  useEffect(() => {
    const syncDismissed = () => {
      setDismissedIds(new Set(readDismissedMatchProductIds()));
    };
    syncDismissed();
    window.addEventListener("homeChatMatchDismissed", syncDismissed);
    window.addEventListener("homeChatMatchUpdated", syncDismissed);
    window.addEventListener("storage", syncDismissed);
    return () => {
      window.removeEventListener("homeChatMatchDismissed", syncDismissed);
      window.removeEventListener("homeChatMatchUpdated", syncDismissed);
      window.removeEventListener("storage", syncDismissed);
    };
  }, []);

  const visibleProducts = useMemo(
    () =>
      sortByRankHighToLow(enrichedProducts).filter(
        (p) => !dismissedIds.has(String(p._id)),
      ),
    [enrichedProducts, dismissedIds, matchScoreRevision],
  );

  const rankedProducts = useMemo(() => {
    if (isFeatured) return visibleProducts.slice(0, 1);
    if (isContinued) {
      const sliceStart = skipFeaturedProduct ? 1 : 0;
      return visibleProducts.slice(
        sliceStart,
        sliceStart + HOME_MATCH_RESULTS_LIMIT,
      );
    }
    return visibleProducts.slice(0, HOME_MATCH_RESULTS_LIMIT);
  }, [visibleProducts, isFeatured, isContinued, skipFeaturedProduct]);

  const compareProducts = useMemo(() => {
    if (isContinued && skipFeaturedProduct) {
      return visibleProducts.slice(0, HOME_MATCH_RESULTS_LIMIT);
    }
    return rankedProducts;
  }, [visibleProducts, isContinued, skipFeaturedProduct, rankedProducts]);

  const handleDismiss = useCallback(
    (product) => {
      const productId = product?._id;
      if (!productId) return;

      const nextIds = addDismissedMatchProductId(productId);
      setDismissedIds(new Set(nextIds));

      const remaining = sortByRankHighToLow(enrichedProducts).filter(
        (p) => !nextIds.includes(String(p._id)),
      );
      if (enrichedProducts.length > 0 && remaining.length === 0) {
        onShortlistExhausted?.();
      }

      const sessionId =
        readHomeChatMatchSessionId() ||
        localStorage.getItem("currentSessionId") ||
        null;

      if (user) {
        trackProduct("dismiss", productId, {
          sessionId,
          surface: "feed_match",
        });
        return;
      }

      if (sessionId) {
        api
          .post("/analytics/interaction", {
            sessionId,
            productId,
            action: "dismiss",
          })
          .catch(() => {});
      }
    },
    [user, trackProduct, enrichedProducts, onShortlistExhausted],
  );

  const effectiveVariant = isContinued ? "default" : variant;
  const bestMatchesClassName =
    effectiveVariant === "hero"
      ? `${styles.bestMatchesSection} ${styles.bestMatchesSectionHero}`
      : styles.bestMatchesSection;
  const skeletonCardStyle =
    effectiveVariant === "hero"
      ? {
          minHeight: isFeatured ? 120 : 280,
          background: "rgba(255, 255, 255, 0.06)",
        }
      : { minHeight: 200, background: "var(--color-bg-tertiary)" };
  const unmatchedMessage = searchParams?.productConstraint?.unmatched
    ? searchParams.productConstraint.message
    : "";
  const sectionTitle =
    sectionTitleProp ||
    unmatchedMessage ||
    (isContinued && skipFeaturedProduct
      ? "Other matches in your shortlist"
      : "Best matches for you");
  const showExtras = !isFeatured && showMarketplace;
  const showMatchList = rankedProducts.length > 0;
  const showCompareAll = showMatchList && compareProducts.length >= 2;
  const topPickProduct = compareProducts[0] ?? null;
  const priceSummary = useMemo(
    () =>
      buildMatchPriceSummary(visibleProducts.slice(0, HOME_MATCH_RESULTS_LIMIT)),
    [visibleProducts],
  );

  const handleCompareAll = useCallback(() => {
    if (compareProducts.length < 2) return;
    navigate("/compare", {
      state: { products: compareProducts, searchParams },
    });
  }, [navigate, compareProducts, searchParams]);

  const compareAllLabel =
    compareProducts.length === 3
      ? "Compare all 3"
      : `Compare all ${compareProducts.length}`;

  const compareAllAction = showCompareAll ? (
    <div className={styles.matchCompareAllFooter}>
      <button
        type="button"
        className={styles.matchCompareAllBtn}
        onClick={handleCompareAll}
      >
        {compareAllLabel}
      </button>
    </div>
  ) : null;

  const renderMatchCards = () =>
    rankedProducts.map((product, index) => {
      const rank =
        product.rank != null && Number.isFinite(Number(product.rank))
          ? Number(product.rank)
          : index + 1;
      return (
        <BestMatchCard
          key={product._id}
          product={product}
          siblings={rankedProducts}
          searchParams={searchParams}
          rank={rank}
          matchScoreRevision={matchScoreRevision}
          featured={isFeatured}
          heroSurface={isFeatured && effectiveVariant === "hero"}
          topPickProduct={topPickProduct}
          onDismiss={handleDismiss}
        />
      );
    });

  if (loading) {
    if (isContinued) return null;

    const skeletonCount = isFeatured ? 1 : 3;

    return (
      <div className={styles.mockStack}>
        {isFeatured ? (
          <div
            className={`${styles.matchCard} ${styles.matchFeedCard} ${styles.matchCardFeatured} ${
              effectiveVariant === "hero" ? styles.matchCardFeaturedHero : ""
            }`.trim()}
            style={skeletonCardStyle}
            aria-hidden="true"
          />
        ) : (
          <section className={bestMatchesClassName}>
            <header className={styles.bestMatchesHeader}>
              <h2 className={styles.bestMatchesTitle}>
                <span className={styles.bestMatchesMark} aria-hidden="true">
                  <FaCheck />
                </span>
                {sectionTitle}
              </h2>
            </header>
            <div className={styles.matchFeedList}>
              {Array.from({ length: skeletonCount }).map((_, i) => (
                <div
                  key={i}
                  className={`${styles.matchCard} ${styles.matchFeedCard}`}
                  style={skeletonCardStyle}
                  aria-hidden="true"
                />
              ))}
            </div>
          </section>
        )}
      </div>
    );
  }

  if (!products.length) {
    if (isFeatured || isContinued) return null;

    if (loadError) {
      return (
        <div
          className={
            emptyHintClassName ? emptyHintClassName : styles.matchEmptyHint
          }
          role="alert"
        >
          <p>Couldn't load your saved match. {loadError}</p>
          {onRetryLoad && (
            <button type="button" onClick={onRetryLoad} className={styles.matchRetryButton}>
              Try again
            </button>
          )}
        </div>
      );
    }

    return (
      <p
        className={
          emptyHintClassName ? emptyHintClassName : styles.matchEmptyHint
        }
      >
        Tell Simpary what you need above — your best matches will show up here.
      </p>
    );
  }

  if (!showMatchList && dismissedIds.size > 0) {
    if (isFeatured || isContinued) return null;

    return (
      <p
        className={
          emptyHintClassName ? emptyHintClassName : styles.matchEmptyHint
        }
      >
        None of these felt right — tell Simpary what went wrong above and we’ll
        rematch.
      </p>
    );
  }

  if (isContinued && !showMatchList) {
    return null;
  }

  if (isContinued && rankedProducts.length === 0) {
    return null;
  }

  if (isFeatured) {
    return <div className={styles.mockStack}>{renderMatchCards()}</div>;
  }

  if (isContinued) {
    return (
      <div className={styles.mockStack}>
        <section className={bestMatchesClassName}>
          {showMatchList && (
            <>
              <header className={styles.bestMatchesHeader}>
                <h2 className={styles.bestMatchesTitle}>
                  <span className={styles.bestMatchesMark} aria-hidden="true">
                    <FaCheck />
                  </span>
                  {sectionTitle}
                </h2>
                {decisionLabel && (
                  <p className={styles.matchDecisionContext}>{decisionLabel}</p>
                )}
                {priceSummary && (
                  <p className={styles.matchPriceSummary}>{priceSummary}</p>
                )}
              </header>
              <div className={styles.matchFeedList}>{renderMatchCards()}</div>
              {compareAllAction}
            </>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className={styles.mockStack}>
      {(showMatchList || !isContinued) && (
        <section className={bestMatchesClassName}>
          {showMatchList && (
            <>
              <header className={styles.bestMatchesHeader}>
                <h2 className={styles.bestMatchesTitle}>
                  <span className={styles.bestMatchesMark} aria-hidden="true">
                    <FaCheck />
                  </span>
                  {sectionTitle}
                </h2>
                {decisionLabel && (
                  <p className={styles.matchDecisionContext}>{decisionLabel}</p>
                )}
                {priceSummary && (
                  <p className={styles.matchPriceSummary}>{priceSummary}</p>
                )}
              </header>
              <div className={styles.matchFeedList}>{renderMatchCards()}</div>
              {compareAllAction}
            </>
          )}
        </section>
      )}

      {showExtras && (
        <MarketplaceListingsSection
          title="Related listings on Marketplace"
          queryParams={{ sort: "recommended" }}
          limit={6}
          compact
          widgetSource="feed_matches"
          variant={effectiveVariant}
        />
      )}
    </div>
  );
};

export default FeedMatchSection;
